"use client";

import { useState, useEffect, useCallback, useMemo, memo } from "react";
import Link from "next/link";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { CategoryFallback } from "@/utils/categoryFallback";
import {
  getSafeCategoryColor,
  getCategoryTextColor,
} from "@/utils/contrastUtils";
import CommonButton from "@/components/common/CommonButton";
import { BlogPost, BlogCategory } from "@/types/blog";
import { clientCache } from "@/lib/client-cache";

// Date formatting utility
const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
};

// Type definitions
interface DropdownItem {
  id: number;
  label: string;
  value: string;
}

interface DropdownNewOldFilterProps {
  onFilterChange: (order: string) => void;
  currentSort: string;
}

interface BlogsListClientProps {
  initialPosts: BlogPost[];
  totalCount: number;
  categories: BlogCategory[];
}

export const DropdownNewOldFilter = memo<DropdownNewOldFilterProps>(
  ({ onFilterChange, currentSort }) => {
    const [isOpen, setOpen] = useState<boolean>(false);
    const items = useMemo<DropdownItem[]>(
      () => [
        { id: 0, label: "Newest", value: "newest" },
        { id: 1, label: "Oldest", value: "oldest" },
      ],
      [],
    );
    const [selectedItem, setSelectedItem] = useState<number>(1);

    useEffect(() => {
      const selected = items.find((item) => item.value === currentSort);
      if (selected) {
        setSelectedItem(selected.id);
      }
    }, [currentSort, items]);

    const toggleDropdown = useCallback(() => setOpen((prev) => !prev), []);

    const handleItemClick = useCallback(
      (id: number, value: string) => {
        setSelectedItem(id);
        onFilterChange(value);
        setOpen(false);
      },
      [onFilterChange],
    );

    const selectedLabel = useMemo(() => {
      return items.find((item) => item.id === selectedItem)?.label || "Newest";
    }, [items, selectedItem]);

    return (
      <div className="relative">
        <button
          type="button"
          className="dropdown-header flex min-w-[120px] cursor-pointer items-center justify-between rounded-[4px] border border-[#6049E7] px-4 py-2 text-base text-[#6049E7] hover:bg-[#6049E7]/5"
          onClick={toggleDropdown}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-label="Sort by"
        >
          {selectedLabel}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="1.5"
            stroke="currentColor"
            className={`ml-2 h-4 w-4 transition-transform duration-200 ${isOpen ? "rotate-180" : ""
              }`}
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="m19.5 8.25-7.5 7.5-7.5-7.5"
            />
          </svg>
        </button>
        {isOpen && (
          <div
            className="absolute left-0 top-full z-10 flex w-full flex-col rounded-b-md border border-gray-300 bg-white shadow-lg"
            role="listbox"
            aria-label="Sort options"
          >
            {items.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => handleItemClick(item.id, item.value)}
                className={`w-full cursor-pointer px-4 py-2 text-left text-base text-[#202020] hover:bg-gray-100 focus:bg-gray-100 focus:outline-none ${item.id === selectedItem ? "bg-gray-100 font-medium" : ""
                  }`}
                role="option"
                aria-selected={item.id === selectedItem}
              >
                {item.label}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  },
);

DropdownNewOldFilter.displayName = "DropdownNewOldFilter";

const BlogsListClient = memo<BlogsListClientProps>(
  ({ initialPosts, totalCount, categories }) => {
    const [posts, setPosts] = useState<BlogPost[]>(initialPosts);
    const [loading, setLoading] = useState<boolean>(false);
    const [page, setPage] = useState<number>(1);
    const [activeTab, setActiveTab] = useState<string>("all-blogs");
    const [sortOrder, setSortOrder] = useState<string>("newest");
    const [searchValue, setSearchValue] = useState<string>("");
    const router = useRouter();

    // Reset posts when filters change
    useEffect(() => {
      setPosts(initialPosts);
      setPage(1);
    }, [initialPosts]);

    const loadMore = useCallback(async () => {
      if (loading) return;

      setLoading(true);
      try {
        const nextPage = page + 1;
        const cacheKey = `blog-posts-${nextPage}-${sortOrder}-${activeTab}`;

        // Check client cache first
        const cachedData = clientCache.get(cacheKey) as {
          posts: BlogPost[];
          totalCount: number;
        } | null;
        if (cachedData) {
          setPosts((prev) => [...prev, ...cachedData.posts]);
          setPage(nextPage);
          setLoading(false);
          return;
        }

        const response = await fetch(
          `/api/blog-posts?page=${nextPage}&sort=${sortOrder}${activeTab !== "all-blogs" ? `&category=${activeTab}` : ""
          }`,
        );

        if (response.ok) {
          const data = await response.json();
          setPosts((prev) => [...prev, ...data.posts]);
          setPage(nextPage);
          // Cache the result
          clientCache.set(cacheKey, data);
        }
      } catch (error) {
        console.error("Error loading more posts:", error);
      } finally {
        setLoading(false);
      }
    }, [page, sortOrder, activeTab, loading]);

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>): void => {
        setSearchValue(e.target.value);
      },
      [],
    );

    const handleSubmitSearch = useCallback(
      (e: React.FormEvent<HTMLFormElement>): void => {
        e.preventDefault();
        if (searchValue.trim()) {
          const cleanSearchTerm = searchValue.trim().replace(/\s+/g, "+");
          router.push(`/blog/search?s=${cleanSearchTerm}`);
        }
      },
      [searchValue, router],
    );

    const handleFilterChange = useCallback(
      async (order: string): Promise<void> => {
        // Don't make API call if it's the same sort order
        if (order === sortOrder) return;

        setSortOrder(order);
        setPage(1);
        setLoading(true);

        const cacheKey = `blog-posts-1-${order}-${activeTab}`;

        // Check client cache first
        const cachedData = clientCache.get(cacheKey) as {
          posts: BlogPost[];
          totalCount: number;
        } | null;
        if (cachedData) {
          setPosts(cachedData.posts);
          setLoading(false);
          return;
        }

        try {
          const response = await fetch(
            `/api/blog-posts?page=1&sort=${order}${activeTab !== "all-blogs" ? `&category=${activeTab}` : ""
            }`,
          );

          if (response.ok) {
            const data = await response.json();
            setPosts(data.posts);
            // Cache the result
            clientCache.set(cacheKey, data);
          }
        } catch (error) {
          console.error("Error filtering posts:", error);
        } finally {
          setLoading(false);
        }
      },
      [activeTab, sortOrder],
    );

    const handleActiveTab = useCallback(
      async (tab: string) => {
        // Don't make API call if it's the same tab
        if (tab === activeTab) return;

        setPage(1);
        setActiveTab(tab);
        setLoading(true);

        const cacheKey = `blog-posts-1-${sortOrder}-${tab}`;

        // Check client cache first
        const cachedData = clientCache.get(cacheKey) as {
          posts: BlogPost[];
          totalCount: number;
        } | null;
        if (cachedData) {
          setPosts(cachedData.posts);
          setLoading(false);
          return;
        }

        try {
          const response = await fetch(
            `/api/blog-posts?page=1&sort=${sortOrder}${tab !== "all-blogs" ? `&category=${tab}` : ""
            }`,
          );

          if (response.ok) {
            const data = await response.json();
            setPosts(data.posts);
            // Cache the result
            clientCache.set(cacheKey, data);
          }
        } catch (error) {
          console.error("Error filtering by category:", error);
        } finally {
          setLoading(false);
        }
      },
      [sortOrder, activeTab],
    );

    // Prefetch data for other categories when component mounts
    useEffect(() => {
      if (categories.length > 0) {
        // Prefetch data for the first few categories
        const categoriesToPrefetch = categories.slice(0, 3);
        categoriesToPrefetch.forEach((category) => {
          if (category.slug !== activeTab) {
            // Prefetch without updating state
            fetch(
              `/api/blog-posts?page=1&sort=${sortOrder}&category=${category.slug}`,
            )
              .then((response) => response.json())
              .catch((error) => console.log("Prefetch error:", error));
          }
        });
      }
    }, [categories, sortOrder, activeTab]);

    return (
      <>
        {loading && (
          <div className="sr-only" aria-live="polite" aria-atomic="true">
            Loading blog posts...
          </div>
        )}
        <div className="relative z-10 flex flex-col-reverse justify-between bg-white py-4 md:sticky md:top-[60px] lg:w-full lg:flex-row">
          <div
            className="flex flex-wrap gap-3"
            role="group"
            aria-label="Blog categories"
          >
            <button
              onClick={() => handleActiveTab("all-blogs")}
              aria-pressed={activeTab === "all-blogs"}
              aria-controls="blog-posts"
              className={`rounded-[4px] border px-4 py-2 text-sm ${activeTab === "all-blogs"
                ? "border-[#6049E7] bg-[#6049E7]/5 text-[#6049E7]"
                : "border-[#717171] bg-white text-[#202020] hover:border-[#6049E7] hover:text-[#6049E7]"
                }`}
            >
              All Blogs
            </button>
            {Array.isArray(categories) && categories?.length ? (
              categories.map((category) => (
                <button
                  key={category.slug}
                  onClick={() => handleActiveTab(category.slug)}
                  aria-pressed={activeTab === category.slug}
                  aria-controls="blog-posts"
                  className={`min-w-[100px] rounded-[4px] border px-4 py-2 text-center text-sm transition-colors duration-200 ${activeTab === category.slug
                    ? "border-[#6049E7] bg-[#6049E7]/5 text-[#6049E7]"
                    : "border-[#717171] bg-white text-[#202020] hover:border-[#6049E7] hover:text-[#6049E7]"
                    }`}
                >
                  {category.title}
                </button>
              ))
            ) : (
              <CategoryFallback isNavbar={false} />
            )}
          </div>
          <div className="mb-4 flex flex-row items-center justify-between gap-4 lg:mb-0">
            <form
              className="w-full sm:max-w-lg"
              onSubmit={handleSubmitSearch}
              role="search"
              aria-label="Search blog posts"
            >
              <div className="relative">
                <label htmlFor="blog-search" className="sr-only">
                  Search blog posts
                </label>
                <div className="pointer-events-none absolute inset-y-0 start-0 flex items-center ps-3">
                  <svg
                    className="h-4 w-4 text-gray-500"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 20 20"
                  >
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"
                    />
                  </svg>
                </div>
                <input
                  type="search"
                  id="blog-search"
                  className="block w-full rounded-lg border border-gray-300 bg-white p-[10px] ps-10 text-sm text-gray-900 focus:border-[#6049E7] focus:outline-none xl:min-w-[360px]"
                  placeholder="Search blog posts..."
                  value={searchValue}
                  onChange={handleChange}
                  aria-describedby="search-help"
                  autoComplete="off"
                />
                <div id="search-help" className="sr-only">
                  Press Enter to search for blog posts
                </div>
              </div>
            </form>
            <div className="relative z-50 flex flex-shrink-0 items-center gap-2 whitespace-nowrap">
              <p className="absolute -top-2 left-2 z-50 bg-white px-1 text-xs font-medium text-[#6049E7]">
                Sort by
              </p>
              <DropdownNewOldFilter
                onFilterChange={handleFilterChange}
                currentSort={sortOrder}
              />
            </div>
          </div>
        </div>
        <div
          id="blog-posts"
          className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3"
          aria-label="Blog posts"
          aria-live="polite"
        >
          {posts.length > 0 && (
            <div className="sr-only" aria-live="polite">
              {posts.length} blog posts loaded
            </div>
          )}
          {posts.length > 0 &&
            posts.map((item, index) => (
              <article
                key={item.id || index}
                className="overflow-hidden rounded-lg border border-gray-200 bg-white transition-shadow duration-300 hover:shadow-lg"
              >
                <Link
                  href={`/blog/${item?.slug}`}
                  className="group block"
                  aria-label={`Read blog post: ${item.title}`}
                >
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <Image
                      src={item.f_image_url ?? "/placeholder-blog.jpg"}
                      alt={item.title || "Blog post image"}
                      fill
                      className="object-cover transition-transform duration-300 group-hover:scale-105"
                      sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                      priority={index < 4}
                      loading={index < 4 ? "eager" : "lazy"}
                      placeholder="blur"
                      blurDataURL="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mP8/x8AAwMCAO6aDPwAAAAASUVORK5CYII="
                    />
                  </div>
                  <div className="flex h-[280px] flex-col p-6">
                    <span
                      className="mb-3 inline-block w-fit rounded-full px-3 py-1 text-sm font-medium"
                      style={{
                        backgroundColor: getSafeCategoryColor(
                          item.category_detail?.color_code,
                        ),
                        color: getCategoryTextColor(
                          item.category_detail?.color_code,
                        ),
                      }}
                    >
                      {item.category_detail?.title}
                    </span>
                    <h3 className="mb-3 line-clamp-2 text-[20px] font-semibold text-gray-800 transition-colors duration-200 group-hover:text-[#6049E7] lg:text-[22px]">
                      <span className="sr-only">Blog post: </span>
                      {item.title}
                    </h3>
                    <p className="mb-4 line-clamp-3 flex-grow text-sm text-gray-600">
                      {item.meta_description || "No description available."}
                    </p>
                    <div className="mt-auto border-t border-gray-200 pt-4">
                      <div className="flex items-center justify-between text-sm text-gray-600">
                        {item.category_detail?.title !== "News" && (<div className="flex items-center gap-3">
                          <div
                            className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-200 font-medium text-gray-600"
                            style={{ color: "#FF3465" }}
                            aria-hidden="true"
                          >
                            {item.creator?.charAt(0) || "A"}
                          </div>
                          <span className="font-medium text-black">
                            {item.creator}
                          </span>
                        </div>)}
                        <time
                          className="font-medium text-black"
                          dateTime={
                            item.publish_date ?? new Date().toISOString()
                          }
                        >
                          {formatDate(
                            item.publish_date ?? new Date().toISOString(),
                          )}
                        </time>
                      </div>
                    </div>
                  </div>
                </Link>
              </article>
            ))}
          {loading &&
            Array.from({ length: 8 }).map((_, index) => (
              <div
                key={`loading-${index}`}
                className="animate-pulse overflow-hidden rounded-lg border border-gray-200 bg-white"
                role="listitem"
                aria-hidden="true"
              >
                <div className="aspect-[4/3] bg-gray-200"></div>
                <div className="flex h-[280px] flex-col p-6">
                  <div className="mb-3 h-4 w-20 rounded-full bg-gray-300"></div>
                  <div className="mb-2 h-5 w-full bg-gray-300"></div>
                  <div className="mb-3 h-5 w-3/4 bg-gray-300"></div>
                  <div className="mb-2 h-4 w-full bg-gray-300"></div>
                  <div className="mb-4 h-4 w-2/3 flex-grow bg-gray-300"></div>
                  <div className="mt-auto border-t border-gray-200 pt-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-full bg-gray-300"></div>
                        <div className="h-4 w-20 bg-gray-300"></div>
                      </div>
                      <div className="h-4 w-24 bg-gray-300"></div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
        </div>
        {totalCount > posts.length && (
          <div className="mb-6 mt-8 flex justify-center">
            <button
              onClick={loadMore}
              disabled={loading}
              className="flex h-[40px] w-fit items-center rounded-[4px] border-none !bg-[#6049E7] px-4 py-[10px] text-[14px] font-medium text-white duration-300 ease-in-out hover:!bg-[#553ED0] disabled:cursor-not-allowed disabled:opacity-50 xl:px-6"
              aria-label="Load more blog posts"
            >
              <span>{loading ? "Loading..." : "Load More"}</span>
            </button>
          </div>
        )}
      </>
    );
  },
);

BlogsListClient.displayName = "BlogsListClient";

export default BlogsListClient;
